import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DeleteOutline as DeleteOutlineIcon,
  PersonOutline,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import {
  useCreateBackendUserBotConnectionMutation,
  useDeleteConnectionMutation,
  useGetBotRegistryQuery,
  useUpdateConnectionMutation,
  type BotRegistryItem,
} from "features/adminUsers/adminUsers";
import { AdminVendorConnection } from "types/interfaces/adminUsers";
import BotsTableScroll, { BOTS_HEADER_CELL_SX } from "components/botsManagement/BotsTableScroll";

/** Tabular metric pill aligned with the Major Jobs page styling. */
function MetaPill({ title, value, avatar }: { title: string; value: string; avatar?: boolean }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "flex-start",
        gap: avatar ? 1 : 0,
        px: 1.25,
        py: 0.85,
        borderRadius: 2,
        bgcolor: (theme) => (theme.palette.mode === "dark" ? "grey.900" : "grey.50"),
        border: 1,
        borderColor: "divider",
        boxShadow: (theme) => (theme.palette.mode === "dark" ? "none" : "0 1px 2px rgba(0,0,0,0.04)"),
      }}
    >
      {avatar ? (
        <Box sx={{ mt: 0.2 }}>
          <PersonOutline color="primary" sx={{ fontSize: 21, opacity: 0.9 }} />
        </Box>
      ) : null}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ lineHeight: 1.1, letterSpacing: 0.06, fontWeight: 600 }}
        >
          {title}
        </Typography>
        <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.35 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

const CENTER_BODY_CELL_SX = { textAlign: "center" as const, verticalAlign: "middle" as const };

type FormState = {
  username: string;
  vendor: string;
  host: string;
  clientCode: string;
  password: string;
  showPw: boolean;
};

const EMPTY_FORM: FormState = {
  username: "",
  vendor: "",
  host: "",
  clientCode: "",
  password: "",
  showPw: false,
};

function normalizeBotRegistryPayload(
  data: { results?: BotRegistryItem[] } | BotRegistryItem[] | undefined | null,
): BotRegistryItem[] {
  if (Array.isArray(data)) return data;
  return data?.results ?? [];
}

export type ConnectionsPanelProps = {
  userId: number;
  userLabel?: string;
  connections: AdminVendorConnection[];
  connectionsLoading?: boolean;
  connectionsError?: boolean;
  onRefreshConnections: () => Promise<unknown>;
  onBack?: () => void;
};

export default function ConnectionsPanel({
  userId,
  userLabel,
  connections,
  connectionsLoading = false,
  connectionsError = false,
  onRefreshConnections,
  onBack,
}: ConnectionsPanelProps) {
  const [createConnection, { isLoading: isCreating }] = useCreateBackendUserBotConnectionMutation();
  const [updateConnection, { isLoading: isSaving }] = useUpdateConnectionMutation();
  const [deleteConnection, { isLoading: isDeleting }] = useDeleteConnectionMutation();

  const { data: botRegistryForVendors } = useGetBotRegistryQuery({ isActive: true, pageSize: 500 });

  const [editTarget, setEditTarget] = useState<AdminVendorConnection | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const vendorSelectOptions = useMemo(() => {
    const rows = normalizeBotRegistryPayload(botRegistryForVendors);
    const s = new Set(rows.map((r) => (r.vendor ?? "").trim()).filter(Boolean));
    const bump = (v: string | undefined) => {
      const t = (v ?? "").trim();
      if (t) s.add(t);
    };
    bump(form.vendor);
    bump(editTarget?.vendor);
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [botRegistryForVendors, form.vendor, editTarget?.vendor]);

  const [rowDeleteTarget, setRowDeleteTarget] = useState<AdminVendorConnection | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);

  useEffect(() => {
    setSelected((prev) => prev.filter((id) => connections.some((c) => c.id === id)));
  }, [connections]);

  const idsOnPage = useMemo(() => connections.map((c) => c.id), [connections]);
  const allSelected = idsOnPage.length > 0 && idsOnPage.every((id) => selected.includes(id));
  const someSelected = idsOnPage.some((id) => selected.includes(id));

  const toggleAll = (checked: boolean) => {
    setSelected((prev) => {
      if (checked) return Array.from(new Set([...prev, ...idsOnPage]));
      const drop = new Set(idsOnPage);
      return prev.filter((id) => !drop.has(id));
    });
  };

  const toggleOne = (id: number, checked: boolean) => {
    setSelected((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const openCreate = () => {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setEditOpen(true);
  };

  const openEdit = (row: AdminVendorConnection) => {
    setEditTarget(row);
    setForm({
      username: row.username ?? "",
      vendor: row.vendor ?? "",
      host: row.host ?? "",
      clientCode: row.client_code ?? "",
      // Backend returns a decrypted password for admin users
      // (AdminVendorConnectionSerializer.get_password) so we pre-fill it here
      // and let the user reveal it via the eye toggle.
      password: row.password ?? "",
      showPw: false,
    });
    setFormError(null);
    setEditOpen(true);
  };

  const save = async () => {
    if (!form.username.trim()) {
      setFormError("Username is required.");
      return;
    }
    if (!form.vendor.trim()) {
      setFormError("Vendor is required.");
      return;
    }
    try {
      if (editTarget) {
        const patch: Partial<AdminVendorConnection> = {
          username: form.username.trim(),
          vendor: form.vendor.trim(),
          host: form.host.trim(),
          client_code: form.clientCode.trim(),
        };
        // Always send the password back: the dialog now pre-fills it from the
        // server, so submitting unchanged should leave the value the same.
        patch.password = form.password;
        await updateConnection({ connectionId: editTarget.id, body: patch }).unwrap();
      } else {
        await createConnection({
          userId,
          body: {
            username: form.username.trim(),
            vendor: form.vendor.trim(),
            host: form.host.trim(),
            client_code: form.clientCode.trim(),
            password: form.password,
            type: "bot",
          },
        }).unwrap();
      }
      await onRefreshConnections();
      setEditOpen(false);
    } catch {
      setFormError(editTarget ? "Failed to save connection." : "Failed to create connection.");
    }
  };

  const confirmRowDelete = async () => {
    if (!rowDeleteTarget) return;
    try {
      await deleteConnection(rowDeleteTarget.id).unwrap();
      setRowDeleteTarget(null);
      await onRefreshConnections();
    } catch {
      /* keep dialog open so the user can see the error via UI alerts */
    }
  };

  const confirmBulkDelete = async () => {
    if (!selected.length) return;
    try {
      for (const cid of selected) {
        await deleteConnection(cid).unwrap();
      }
      setSelected([]);
      setBulkDeleteOpen(false);
      await onRefreshConnections();
    } catch {
      /* error: leave dialog open */
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minHeight: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Connections
          </Typography>
          <Box sx={{ mt: 0.75 }}>
            <MetaPill
              title="User"
              value={userLabel?.trim() ? userLabel.trim() : "User"}
              avatar
            />
          </Box>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button size="small" variant="contained" onClick={openCreate} disabled={isCreating}>
            New Connection
          </Button>
          <Button
            size="small"
            color="error"
            variant="outlined"
            onClick={() => setBulkDeleteOpen(true)}
            disabled={!selected.length || isDeleting}
          >
            Delete selected
          </Button>
          {onBack && (
            <Button size="small" variant="outlined" onClick={onBack}>
              Back to Major Jobs list
            </Button>
          )}
        </Stack>
      </Box>

      {connectionsError && (
        <Alert severity="error">Failed to load connections for this user.</Alert>
      )}

      <BotsTableScroll fill>
      <Table size="small" stickyHeader sx={{ tableLayout: "fixed", minWidth: 720 }}>
        <colgroup>
          <col style={{ width: 48 }} />
          <col style={{ width: 200 }} />
          <col style={{ width: 140 }} />
          <col />
          <col style={{ width: 150 }} />
          <col style={{ width: 150 }} />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" sx={BOTS_HEADER_CELL_SX}>
              <Checkbox
                size="small"
                indeterminate={someSelected && !allSelected}
                checked={allSelected}
                disabled={!connections.length}
                onChange={(e) => toggleAll(e.target.checked)}
                inputProps={{ "aria-label": "Select all connections" }}
                sx={{ p: 0.5 }}
              />
            </TableCell>
            <TableCell sx={BOTS_HEADER_CELL_SX}>Login</TableCell>
            <TableCell sx={BOTS_HEADER_CELL_SX}>Vendor</TableCell>
            <TableCell sx={BOTS_HEADER_CELL_SX}>Host</TableCell>
            <TableCell sx={BOTS_HEADER_CELL_SX}>Client code</TableCell>
            <TableCell sx={BOTS_HEADER_CELL_SX}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {connectionsLoading ? (
            <TableRow>
              <TableCell colSpan={6} sx={{ textAlign: "center", py: 3 }}>
                <CircularProgress size={22} />
              </TableCell>
            </TableRow>
          ) : connections.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} sx={{ textAlign: "center", py: 3 }}>
                <Typography color="text.secondary">No bot connections for this user yet.</Typography>
              </TableCell>
            </TableRow>
          ) : (
            connections.map((row) => {
              const usernameLabel = row.username?.trim() ? `@${row.username.trim()}` : "-";
              const hostLabel =
                row.host?.trim() || `default ${row.vendor?.trim() || "vendor"} host`;
              return (
                <TableRow key={row.id} hover>
                  <TableCell padding="checkbox" sx={CENTER_BODY_CELL_SX}>
                    <Checkbox
                      size="small"
                      checked={selected.includes(row.id)}
                      onChange={(e) => toggleOne(row.id, e.target.checked)}
                      sx={{ p: 0.5 }}
                    />
                  </TableCell>
                  <TableCell sx={CENTER_BODY_CELL_SX}>
                    <Typography variant="body2" noWrap title={usernameLabel}>
                      {usernameLabel}
                    </Typography>
                  </TableCell>
                  <TableCell sx={CENTER_BODY_CELL_SX}>
                    <Typography variant="body2" noWrap title={row.vendor}>
                      {row.vendor}
                    </Typography>
                  </TableCell>
                  <TableCell sx={CENTER_BODY_CELL_SX}>
                    <Typography
                      variant="body2"
                      noWrap
                      title={hostLabel}
                      color={row.host?.trim() ? "text.primary" : "text.secondary"}
                    >
                      {hostLabel}
                    </Typography>
                  </TableCell>
                  <TableCell sx={CENTER_BODY_CELL_SX}>
                    <Typography variant="body2" noWrap title={row.client_code ?? ""}>
                      {row.client_code?.trim() || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={CENTER_BODY_CELL_SX}>
                    <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                      <Button size="small" onClick={() => openEdit(row)} disabled={isSaving}>
                        Edit
                      </Button>
                      <Tooltip title="Delete connection">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            aria-label="Delete connection"
                            onClick={() => setRowDeleteTarget(row)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      </BotsTableScroll>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editTarget ? "Edit Connection" : "New Connection"}</DialogTitle>
        <DialogContent>
          <TextField
            sx={{ mt: 1, mb: 1.5 }}
            fullWidth
            label="Username"
            required
            value={form.username}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setForm((p) => ({ ...p, username: e.target.value }))}
          />
          <Autocomplete
            sx={{ mb: 1.5 }}
            freeSolo
            options={vendorSelectOptions}
            value={form.vendor}
            onInputChange={(_e, newValue) =>
              setForm((p) => ({ ...p, vendor: newValue ?? "" }))
            }
            ListboxProps={{ style: { maxHeight: 280 } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Vendor"
                required
                error={!form.vendor.trim() && Boolean(formError)}
              />
            )}
          />
          <TextField
            sx={{ mb: 1.5 }}
            fullWidth
            label="Host"
            value={form.host}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setForm((p) => ({ ...p, host: e.target.value }))}
            placeholder={`default ${form.vendor?.trim() || "vendor"} host`}
          />
          <TextField
            sx={{ mb: 1.5 }}
            fullWidth
            label="Client code"
            value={form.clientCode}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setForm((p) => ({ ...p, clientCode: e.target.value }))
            }
          />
          <TextField
            fullWidth
            label="Password"
            type={form.showPw ? "text" : "password"}
            value={form.password}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setForm((p) => ({ ...p, password: e.target.value }))
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() => setForm((p) => ({ ...p, showPw: !p.showPw }))}
                    aria-label="toggle password visibility"
                  >
                    {form.showPw ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {formError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {formError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void save()} disabled={isSaving || isCreating}>
            {isSaving || isCreating ? "Saving..." : editTarget ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rowDeleteTarget != null} onClose={() => setRowDeleteTarget(null)}>
        <DialogTitle>Delete connection?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {rowDeleteTarget ? (
              <>
                Delete connection{" "}
                <strong>
                  {rowDeleteTarget.username?.trim() ? `@${rowDeleteTarget.username.trim()}` : "-"}
                </strong>{" "}
                ({rowDeleteTarget.vendor})? This cannot be undone.
              </>
            ) : null}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRowDeleteTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={isDeleting || rowDeleteTarget == null}
            onClick={() => void confirmRowDelete()}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDeleteOpen} onClose={() => !isDeleting && setBulkDeleteOpen(false)}>
        <DialogTitle>Delete selected connections?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete {selected.length} selected connection{selected.length === 1 ? "" : "s"}? This cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={!selected.length || isDeleting}
            onClick={() => void confirmBulkDelete()}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
